import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} PaymentProof
 * @property {number} id
 * @property {string} name
 * @property {string} type
 * @property {string} url
 * @property {string} uploaded_at
 * @property {number} uploaded_by
 */

/**
 * @typedef {object} Invoice
 * @property {number} id
 * @property {string} invoice_no
 * @property {string} total_amount
 * @property {string} paid_amount
 * @property {string} status
 * @property {string} issued_at
 * @property {string} due_at
 */

/**
 * @typedef {object} PaymentDetails
 * @property {number} id
 * @property {number} invoice_id
 * @property {string} amount
 * @property {"offline" | "online"} source
 * @property {string} method
 * @property {"paid" | "pending" | "failed"} status
 * @property {string|null} reference_id
 * @property {boolean} is_verified
 * @property {string} created_at
 * @property {string} processed_at
 * @property {string|null} invoice_pdf_path
 * @property {PaymentProof[]} proofs
 * @property {Invoice} invoice
 * @property {string} currency
 */

export const useGetPaymentById = () => {
  const [payment, setPayment] = useState(
    /** @type {PaymentDetails | null} */(null)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch single payment by ID
   * @async
   * @param {number|string} paymentId
   */
  const getPaymentById = async (paymentId) => {
    if (!paymentId) return;

    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/billing/payments/${paymentId}`;
      const response = await axiosInstance.get(url);

      setPayment(response?.data?.data ?? null);
    } catch (err) {
      setPayment(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    payment,
    loading,
    error,
    getPaymentById,
  };
};
